import type { Environment } from "../../../client/stores";
import { mediaServerCatalogImage, mediaServerPlaylistImage } from "../../../client/utils";
import * as Webapi from "../generated/webapi.g";
import { ImageTileProps } from "../ui/ImageTile.component";
import { slugify } from "./common";

export enum MusicPageType {
    RECENT = "recentlyplayed",
    TOP = "topsongs",
}

export interface Playlist {
    id: string;
    userId: string;
    name: string;
    description: string;
    author: string;
    urls: {
        web: string;
        image: string;
        play: string;
        goto: string;
    };
    reportingKey: string;
}

export const convertPodcast = (env: Environment, podcast: Webapi.PodcastFragment): ImageTileProps => ({
    title: podcast.title,
    url: `${env.NATIONAL_SITE_URL}/podcast/${podcast.slug}-${podcast.id}/`,
    imageUrl: mediaServerCatalogImage(env.IMAGE_HOST, "podcast", podcast.id, 224, 224),
});

export const convertPlaylist = (env: Environment, playlist: Playlist): ImageTileProps => ({
    title: playlist.name,
    url: playlist.urls.web,
    author: playlist.author,
    imageUrl: mediaServerPlaylistImage(env.IMAGE_HOST, playlist.userId, playlist.id, 224, 224),
});

export enum CatalogKindTypes {
    artist = "artist",
    album = "album",
    station = "station",
    podcast = "podcast",
    curated = "curated",
    song = "song",
}

export interface CatalogUrlProps {
    track?: Webapi.TrackDetailFragment | Webapi.CurrentlyPlayingTrackDetailFragment;
    id?: string | number;
    related_id?: string | number;
    kind: CatalogKindTypes;
    embed?: boolean;
}

export type CatalogKind = "album" | "artist" | "track" | "station" | "podcast" | "curated";

export function isCatalogKind(s: string): s is CatalogKind {
    // Including talk for compatibility.
    return ["album", "artist", "track", "station", "podcast", "curated", "talkShow"].includes(s);
}

export function imageURI(IMAGE_URI_BASE: string, kind: string, id: string | number) {
    if (kind === "station") {
        return `https://${IMAGE_URI_BASE}/v3/catalog/live/${id}`;
    }
    if (kind === "album") {
        return `https://${IMAGE_URI_BASE}/v3/catalog/album/${id}`;
    }
    if (kind === "talkShow" || kind === "podcast") {
        return `https://${IMAGE_URI_BASE}/v3/catalog/show/${id}`;
    }
    if (kind === "curated") {
        const parts = (id as string).split("::");
        return `https://${IMAGE_URI_BASE}/v3/user/${parts[0]}/collection/${parts[1]}`;
    }
    if (isCatalogKind(kind)) {
        return `https://${IMAGE_URI_BASE}/v3/catalog/${kind}/${id}`;
    }
    return undefined;
}

export const generateCatalogUrl = (env: Environment, props: CatalogUrlProps) => {
    let url = "";
    if (props.track) {
        const { track } = props;
        const artistSlug = `${slugify(track.artist?.artistName)}-${track.artistId}`;
        switch (props.kind) {
            case CatalogKindTypes.artist:
                url = `${env.NATIONAL_SITE_URL}/artist/${artistSlug}/`;
                break;
            case CatalogKindTypes.album:
                const albumSlug = `${slugify(track?.albumName)}-${track.albumId}`;
                url = `${env.NATIONAL_SITE_URL}/artist/${artistSlug}/albums/${albumSlug}/`;
                break;
            case CatalogKindTypes.song:
                const songSlug = `${slugify(track.title)}-${track.trackId}`;
                url = `${env.NATIONAL_SITE_URL}/artist/${artistSlug}/songs/${songSlug}/`;
                break;
        }
    } else if (props.id) {
        switch (props.kind) {
            case CatalogKindTypes.artist:
                url = `${env.NATIONAL_SITE_URL}/artist/${props.id}/`;
                break;
            case CatalogKindTypes.station:
                url = `${env.NATIONAL_SITE_URL}/live/${props.id}/`;
                break;
            case CatalogKindTypes.podcast:
                if (props.related_id) {
                    url = `${env.NATIONAL_SITE_URL}/podcast/${props.id}/episode/${props.related_id}/`;
                } else {
                    url = `${env.NATIONAL_SITE_URL}/podcast/${props.id}/`;
                }
                break;
            case CatalogKindTypes.curated:
                const [id, hash] = props.id.toString().split("::");
                url = `${env.NATIONAL_SITE_URL}/playlist/${id}-${hash}/`;
                break;
        }
    }
    if (!url || !props.embed) {
        return url;
    }
    const urlWithQueryParams = new URL(url);
    urlWithQueryParams.searchParams.append("ihrnetwork", "true");
    urlWithQueryParams.searchParams.append("embed", "true");
    return urlWithQueryParams.toString();
};
